/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, ArrowRightCircle } from 'lucide-react';
import apiClient from '../api/client';
import Pagination from '../components/Pagination';
import StatusBadge from '../components/StatusBadge';
import { formatDateTime } from '../utils/dateTime';

const LIMIT = 10;

const ImportHistory = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const navigate = useNavigate();

  const fetchHistory = useCallback(async (page = 1) => {
    try {
      setLoading(true);
      setError('');
      const { data } = await apiClient.get('/import-history', {
        params: { page, limit: LIMIT },
      });
      setRecords(data.data);
      setTotalPages(data.pagination.totalPages);
      setCurrentPage(data.pagination.page);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load import history');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory(currentPage);
  }, [fetchHistory, currentPage]);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 mb-4">
        <div>
          <h1 className="page-title">Import History</h1>
          <p className="page-subtitle">View all bulk assignment import logs</p>
        </div>
        <button
          onClick={() => fetchHistory(currentPage)}
          className="btn btn-square btn-ghost bg-base-content/5 border border-base-content/10 hover:bg-base-content/10 rounded-xl transition-all"
          title="Refresh"
        >
          <RefreshCw size={18} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="bg-error/10 border border-error/20 text-error px-4 py-3 rounded-lg text-sm font-bold">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex flex-col items-center justify-center py-32 space-y-4">
          <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin" />
          <p className="text-base-content/40 font-black uppercase tracking-widest text-xs animate-pulse">
            Loading import history...
          </p>
        </div>
      ) : records.length === 0 ? (
        <div className="text-center py-32 glass-panel !bg-base-100/30 rounded-xl border-dashed border-2 border-base-content/10">
          <div className="w-20 h-20 bg-base-200 rounded-xl flex items-center justify-center mx-auto mb-6 shadow-inner">
            <span className="text-4xl text-base-content/30">📂</span>
          </div>
          <h3 className="text-2xl font-black text-base-content mb-2 tracking-tight">No import history</h3>
          <p className="text-base-content/50 max-w-xs mx-auto font-bold uppercase tracking-widest text-[10px]">
            Bulk imports will appear here once created
          </p>
        </div>
      ) : (
        <div className="glass-panel rounded-xl overflow-hidden overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead>
              <tr className="border-b border-base-content/10">
                {['Original File', 'Result File', 'Total Records', 'Success Records', 'Failed Records', 'Import At', 'Status', 'Action'].map((h) => (
                  <th
                    key={h}
                    className="px-5 py-3 text-left text-[10px] font-black uppercase tracking-widest text-base-content/30"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {records.map((record) => (
                <tr
                  key={record.id}
                  className="border-b border-base-content/5 hover:bg-base-content/5 transition-colors"
                >
                  <td className="px-5 py-4 font-bold text-base-content/70">
                    {record.originalCsvFile
                      ? <a href={record.originalCsvFile} target="_blank" rel="noreferrer" className="link link-primary">{record.originalFileName || 'Download'}</a>
                      : '—'}
                  </td>
                  <td className="px-5 py-4 font-bold text-base-content/70">
                    {record.resultCsvFile
                      ? <a href={record.resultCsvFile} target="_blank" rel="noreferrer" className="link link-primary">{record.resultFileName || 'Download'}</a>
                      : '—'}
                  </td>
                  <td className="px-5 py-4 font-bold text-base-content/70">
                    {record.totalRecords ?? '—'}
                  </td>
                  <td className="px-5 py-4 font-bold text-success">
                    {record.successRecordCount ?? '—'}
                  </td>
                  <td className="px-5 py-4 font-bold text-error">
                    {record.failedRecordCount ?? '—'}
                  </td>
                  <td className="px-5 py-4 text-base-content/50 font-bold">
                    {formatDateTime(record.createdAt)}
                  </td>
                  <td className="px-5 py-4 min-w-[130px]">
                    <StatusBadge status={record.importStatus} />
                  </td>
                  <td className="px-5 py-4 min-w-[140px]">
                    {record.importStatus === 'confirmation_pending' && (
                      <button
                        onClick={(e) => { e.stopPropagation(); navigate(`/assignments/confirm-import/${record.id}`); }}
                        className="inline-flex items-center flex-nowrap gap-1.5 btn btn-sm btn-primary rounded-lg text-xs font-bold whitespace-nowrap"
                      >
                        <ArrowRightCircle size={14} className="shrink-0" />
                        Proceed
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="px-4">
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={(page) => setCurrentPage(page)}
          loading={loading}
        />
      </div>
    </div>
  );
};

export default ImportHistory;
